'use client';

import React, { useEffect } from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { LOCATION } from 'src/hooks/filters/constants';
import { useStrategyForm } from 'src/hooks/strategy/useStrategyForm';
import { useTemplateHydration } from 'src/hooks/strategy/useTemplateHydration';
import { useAiGenerator } from 'src/hooks/aiGenerator/useAiGenerator';
import { StrategyType } from 'src/redux/slices/strategyFormSlice';
import {
  getStrategyConfig,
  getStrategyConfigComponent,
} from 'src/components/Strategy/config';
import { StrategyTimeFrame } from 'src/components/Strategy/Admin/AddEditStrategy/components/StrategyTimeFrame';

const TargetStrategies: React.FC = () => {
  const {
    selectedPlay,
    selectedType,
    setStrategyType,
    formData,
    updateRefreshEvent,
    updateMaxOpportunities,
  } = useStrategyForm();
  const { hydrateTemplate } = useTemplateHydration(LOCATION.STRATEGY);
  const { dynamicCriteria } = useAiGenerator(LOCATION.STRATEGY);

  const targetTemplate = selectedPlay?.target_template;
  const templateType = targetTemplate?.type;
  const config = targetTemplate?.config;

  // Hydrate form data when selectedPlay changes
  // Only hydrate if there's no existing criteria (criteria is cleared when user selects a new play)
  useEffect(() => {
    if (!config || !templateType || !selectedPlay) return;

    // Set the strategy type based on template type
    setStrategyType(templateType as StrategyType);

    // Only hydrate if we don't have any criteria yet
    // When user selects a new play, criteria is cleared in ChoosePlayStep
    const hasExistingCriteria =
      dynamicCriteria?.criteria_template ||
      (dynamicCriteria?.criteria_inputs &&
        Object.keys(dynamicCriteria.criteria_inputs).length > 0);

    if (!hasExistingCriteria) {
      hydrateTemplate(config, templateType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config, templateType, selectedPlay]);

  const supportsRefresh =
    getStrategyConfig(selectedType)?.features?.supportsRefresh ?? false;

  const handleMaxOpportunitiesChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    if (value === '') {
      updateMaxOpportunities('' as any);
    } else if (/^\d+$/.test(value)) {
      const numValue = parseInt(value);
      if (numValue >= 1 && numValue <= 1000) {
        updateMaxOpportunities(numValue);
      }
    }
  };

  const handleMaxOpportunitiesBlur = (
    e: React.FocusEvent<HTMLInputElement>
  ) => {
    let value = parseInt(e.target.value) || 100;
    if (value < 1) value = 1;
    if (value > 1000) value = 1000;
    updateMaxOpportunities(value);
  };

  // Get the configuration component from registry
  const ConfigComponent = getStrategyConfigComponent(selectedType);

  // Render strategy configuration component from registry
  const renderStrategyTypeContent = () => {
    if (!ConfigComponent) {
      return null;
    }

    return <ConfigComponent location={LOCATION.STRATEGY} />;
  };

  if (!selectedPlay) {
    return (
      <div className="flex flex-col gap-4" data-testid="target-strategies">
        <p className="text-sm text-tertiary">No play selected</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4" data-testid="target-strategies">
      {/* Header */}
      <div className="flex flex-col gap-1 border-b border-neutral-200 pb-4">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-primary">
          Targeting
        </h2>
        {targetTemplate && (
          <p className="text-sm text-secondary">{targetTemplate.name}</p>
        )}
      </div>

      {/* Info Callout */}
      {targetTemplate?.description && (
        <div
          className="flex items-start gap-3 rounded-lg border-l-4 border-sky-500 bg-sky-50 p-4"
          role="note"
          aria-label="Targeting information"
          data-testid="targeting-info-callout"
        >
          <Icon
            icon={faCircleInfo}
            className="mt-0.5 shrink-0 text-sky-600"
            aria-hidden="true"
          />
          <p className="text-sm text-sky-800">{targetTemplate.description}</p>
        </div>
      )}

      {/* Strategy Configuration from Registry */}
      {renderStrategyTypeContent()}

      {/* Target Strategy Settings */}
      <div
        className="flex items-start gap-6 rounded-xl border border-neutral-200 bg-neutral-50 p-4"
        data-testid="target-strategy-settings"
        aria-label="Target strategy settings"
      >
        {supportsRefresh && (
          <StrategyTimeFrame
            selectedTimeframe={formData.refresh_event}
            onSelect={(timeframe) =>
              updateRefreshEvent(timeframe as 'NEVER' | 'MONTHLY')
            }
          />
        )}
        <div className="flex flex-col gap-1">
          <label className="text-sm font-semibold text-primary">
            Max Opportunities
          </label>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={formData.max_opportunities}
              onChange={handleMaxOpportunitiesChange}
              onBlur={handleMaxOpportunitiesBlur}
              className="w-20 rounded-full border border-neutral-200 bg-white px-3 py-2 text-center font-medium focus:border-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500"
              data-testid="target-max-opportunities-input"
              aria-label="Target max opportunities"
            />
            <span className="text-xs text-neutral-500">Range: 1-1000</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TargetStrategies;
